import PropTypes from 'prop-types';
import EventListImpl from './event-list.js';
import MojitoCore from 'mojito/core';
import { noop } from 'mojito/utils';

const UIView = MojitoCore.Presentation.UIView;

/**
 * EventList is a view responsible for displaying events in a list format.
 * The information it displays pertains to in-play details such as scores,
 * times, and preferred markets.
 * It showcases its responsiveness by conforming its layout to the available width.
 *
 * @class EventList
 * @name module
 * @memberof Mojito.Modules.EventList
 */

/**
 * EventList prop types.
 *
 * @property {string} [headerText] - Text used as header. If not provided then sport name will be used.
 * @property {Function} [onItemClick = ()=>{}] - Callback fired when an item is clicked.
 * @property {Function} [onDataLoad] - Callback triggered when the market group is loaded.
 * @property {string} eventGroupsId - Event groups id.
 * @property {Array<Mojito.Services.EventGroups.types.EventGroup>} eventGroups - Groups of events to show.
 * @property {number} totalEventsCount - Total number of events to show.
 * @property {Array<string>} [initialExpandedGroupIds = []] - The list of initially expanded group ids.
 * @property {Array<Mojito.Services.EventGroups.types.MarketOption>} [marketOptions = []] - Market options list.
 * @property {string} sportId - Sport id.
 * @property {Function} [onShowAllButtonClick = ()=>{}] - Callback triggered when 'show all events' button is clicked.
 * @property {boolean} [isInPlay = false] - Flag to indicate if the list is for live events.
 * @property {boolean} [isLiveAndFuture = false] - Flag to indicate if the list contains both live and future events. When true, per-event navigation is resolved based on each event's inPlay status.
 * @property {boolean} [enableSorting = false] - Flag to enable sorting mechanism.
 * @property {boolean} [redirectToTypePage] - When true, coupon navigation goes to the sport type page; otherwise to the coupon page.
 * @property {string} [eventGroupName] - CMS coupon name used to resolve the navigation button label.
 * @property {boolean} [isCouponNavigation] - When true, navigation goes to the coupon or type page.
 * @property {boolean} [showAllEventsButton] - Whether to show the navigation button. When omitted, falls back to config.
 * @property {object | Function} [elementRef] - A ref to the rendered element.
 *
 * @memberof Mojito.Modules.EventList.module
 */
const propTypes = {
    headerText: PropTypes.string,
    onItemClick: PropTypes.func,
    onDataLoad: PropTypes.func,
    eventGroupsId: PropTypes.string,
    eventGroups: PropTypes.array.isRequired,
    totalEventsCount: PropTypes.number,
    initialExpandedGroupIds: PropTypes.array,
    marketOptions: PropTypes.array,
    sportId: PropTypes.string.isRequired,
    onShowAllButtonClick: PropTypes.func,
    isInPlay: PropTypes.bool,
    isLiveAndFuture: PropTypes.bool,
    enableSorting: PropTypes.bool,
    redirectToTypePage: PropTypes.bool,
    eventGroupName: PropTypes.string,
    isCouponNavigation: PropTypes.bool,
    showAllEventsButton: PropTypes.bool,
    elementRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

const defaultProps = {
    isInPlay: false,
    isLiveAndFuture: false,
    marketOptions: [],
    initialExpandedGroupIds: [],
    onDataLoad: noop,
    onItemClick: noop,
    onShowAllButtonClick: noop,
    enableSorting: false,
};

export default UIView('EventList', EventListImpl, propTypes, defaultProps);
